import React from "react";

//BinaryForge Components
import { Field } from "../../";

//Helpers
import { getYesNoString } from "../../../helpers/Device";

const AlarmRowsTable = ({ rows }) => {
	if (!rows?.length) {
		return null;
	}

	return (
		<div style={{ overflowX: "auto" }}>
			<div style={{ minWidth: "max(100%, 30rem)" }}>
				<div
					className="grid gapCol-medium gapRow-xsmall fontWeight-bold marginBottom-small"
					style={{
						gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
					}}
				>
					<span>Alarm</span>
					<span>Enabled</span>
					<span>Threshold</span>
					<span>Audible</span>
				</div>
				{rows.map((row) => (
					<div
						key={row.id}
						className="grid gapCol-medium gapRow-xsmall marginBottom-xsmall"
						style={{
							gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
						}}
					>
						<span>{row.alarm}</span>
						<span>{row.enabled}</span>
						<span>{row.threshold}</span>
						<span>{row.audible}</span>
					</div>
				))}
			</div>
		</div>
	);
};

const getValveTypeLabel = (type) => {
	if (type === 0) {
		return "Latching (pulse)";
	}
	if (type === 1) {
		return "Motorised (constant)";
	}
	return "---";
};

const ValveRowsTable = ({ fs2Config }) => {
	const rows = [
		{
			id: "valve1",
			valve: "Valve 1",
			enabled: getYesNoString(fs2Config?.valves?.valve1?.enabled),
			type: getValveTypeLabel(fs2Config?.valves?.valve1?.type),
			invert: getYesNoString(fs2Config?.valves?.valve1?.invert),
			pulsesPerL: fs2Config?.valves?.valve1?.pulses_per_l ?? "---",
		},
		{
			id: "valve2",
			valve: "Valve 2",
			enabled: getYesNoString(fs2Config?.valves?.valve2?.enabled),
			type: getValveTypeLabel(fs2Config?.valves?.valve2?.type),
			invert: getYesNoString(fs2Config?.valves?.valve2?.invert),
			pulsesPerL: fs2Config?.valves?.valve2?.pulses_per_l ?? "---",
		},
	];

	return (
		<div>
			<div
				className="grid gapCol-medium gapRow-xsmall fontWeight-bold marginBottom-small"
				style={{
					gridTemplateColumns:
						"minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr)",
				}}
			>
				<span>Valve</span>
				<span>Enabled</span>
				<span>Type</span>
				<span>Invert</span>
				<span>Pulses/L</span>
			</div>
			{rows.map((row) => (
				<div
					key={row.id}
					className="grid gapCol-medium gapRow-xsmall marginBottom-xsmall"
					style={{
						gridTemplateColumns:
							"minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr)",
					}}
				>
					<span>{row.valve}</span>
					<span>{row.enabled}</span>
					<span>{row.type}</span>
					<span>{row.invert}</span>
					<span>{row.pulsesPerL}</span>
				</div>
			))}
		</div>
	);
};

const OutputRowsTable = ({ fs2Config, outputModeLabels }) => {
	const rows = [
		{
			id: "relay1",
			output: "Output Relay 1",
			enabled: getYesNoString(fs2Config?.outputs?.relay1?.enabled),
			mode: outputModeLabels.relay1,
		},
		{
			id: "relay2",
			output: "Output Relay 2",
			enabled: getYesNoString(fs2Config?.outputs?.relay2?.enabled),
			mode: outputModeLabels.relay2,
		},
	];

	return (
		<div style={{ overflowX: "auto" }}>
			<div style={{ minWidth: "max(100%, 24rem)" }}>
				<div
					className="grid gapCol-medium gapRow-xsmall fontWeight-bold marginBottom-small"
					style={{ gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)" }}
				>
					<span>Output</span>
					<span>Enabled</span>
					<span>Mode</span>
				</div>
				{rows.map((row) => (
					<div
						key={row.id}
						className="grid gapCol-medium gapRow-xsmall marginBottom-xsmall"
						style={{ gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)" }}
					>
						<span>{row.output}</span>
						<span>{row.enabled}</span>
						<span>{row.mode}</span>
					</div>
				))}
			</div>
		</div>
	);
};

const DeviceDetailsConfigSection = ({ t, viewModel }) => {
	const { config, supports } = viewModel;
	const { reportedState, wakeIntervalValue, keepaliveIntervalValue, fs2Config, alarmsTableRows, outputModeLabels } =
		config;
	const isFs2 = Boolean(supports?.isFs2);
	const showLegacyVdcConfig = !supports?.isFs2 && reportedState?.dc_power?.enabled !== undefined;
	// const showFs2ModbusReserved = isFs2;
	const showFs2ModbusReserved = false;

	return (
		<div className="card">
			<header>
				<h3>{t("device.config.header")}</h3>
			</header>
			<div className="flexVert gap-medium">
				{!isFs2 && (
					<section className="deviceDetailsSubsection">
						<h4 className="deviceDetailsSubsectionTitle">Intervals and Power</h4>
						<div className="grid columns-2-AutoFree gapCol-small gapRow-xsmall">
							{wakeIntervalValue !== null && (
								<Field
									label={t("device.config.interval.wakeInterval")}
									highlight
									response={wakeIntervalValue}
									responseUnit={t("device.unit.seconds")}
								/>
							)}
							{keepaliveIntervalValue !== null && (
								<Field
									label={t("device.config.interval.keepInterval")}
									highlight
									response={keepaliveIntervalValue}
									responseUnit={t("device.unit.seconds")}
								/>
							)}
							<Field
								label={t("device.config.batt")}
								highlight
								response={getYesNoString(reportedState.battery?.enabled)}
								responseClass={[
									"fieldBoolean",
									`fieldBoolean-${
										reportedState.battery?.enabled
											? Boolean(reportedState.battery?.enabled)
											: "undefined"
									}`,
								]}
							/>
							{showLegacyVdcConfig && (
								<Field
									label={t("device.config.vdc")}
									response={getYesNoString(reportedState.dc_power?.enabled)}
									responseClass={[
										"fieldBoolean",
										`fieldBoolean-${
											reportedState.dc_power?.enabled
												? Boolean(reportedState.dc_power?.enabled)
												: "undefined"
										}`,
									]}
								/>
							)}
							<Field
								label={t("device.config.modbus")}
								highlight
								response={getYesNoString(reportedState.modbus?.enabled)}
								responseClass={[
									"fieldBoolean",
									`fieldBoolean-${
										reportedState.modbus?.enabled
											? Boolean(reportedState.modbus?.enabled)
											: "undefined"
									}`,
								]}
							/>
						</div>
					</section>
				)}

				{fs2Config && (
					<div className="grid columns-2 gapCol-large gapRow-medium aItems-start">
						<section className="deviceDetailsSubsection">
							<h4 className="deviceDetailsSubsectionTitle">{t("device.config.sections.valves")}</h4>
							<ValveRowsTable fs2Config={fs2Config} />
						</section>

						<section className="deviceDetailsSubsection">
							<h4 className="deviceDetailsSubsectionTitle">{t("device.config.sections.outputs")}</h4>
							<OutputRowsTable fs2Config={fs2Config} outputModeLabels={outputModeLabels} />
							{showFs2ModbusReserved && (
								<div className="grid columns-2-AutoFree gapCol-small gapRow-xsmall marginTop-small">
									<Field label={t("device.config.modbus")} response="Reserved (not in use)" />
								</div>
							)}
						</section>

						<section className="deviceDetailsSubsection" style={{ gridColumn: "1 / -1" }}>
							<h4 className="deviceDetailsSubsectionTitle">
								{t("device.config.sections.alarmsMaintenance")}
							</h4>
							<AlarmRowsTable rows={alarmsTableRows} />
							<div className="grid columns-2-AutoFree gapCol-small gapRow-xsmall marginTop-small">
								<Field
									label="Valve Exercise Enabled"
									highlight
									response={getYesNoString(fs2Config?.maintenance?.valve_exercise_enabled)}
								/>
							</div>
						</section>
					</div>
				)}
			</div>
		</div>
	);
};

export default DeviceDetailsConfigSection;
